// ============================================================================
// SidebarCool — sidebar del Layout "cool" (Navegación C / hombro grande).
//
// Diferencias con el Sidebar clásico:
//   • No tiene su propio header (logo + Salonika vive en el codo del topbar).
//   • Width fijo 236px (igual al codo, para que la marca arriba se alinee
//     verticalmente con los items).
//   • Items con padding 11/15, gap 13, radius 13, font 14.5.
//   • Activo: fondo blanco, color azul-700, sombra fuerte (8 18 .3).
//   • Inactivo: transparente; hover rgba(255,255,255,.12).
//
// El contenedor padre debe ser el gradient azul — la sidebar no pinta su
// propio bg.
// ============================================================================
import {
  Home, ShoppingCart, Calendar, LayoutGrid, Package, Users, BarChart3,
  BookOpen, Settings, Clock, FileText, Building2, Bug, Code2,
  TrendingDown, Receipt, Gauge, Wallet, X, ChevronsLeft, ChevronsRight, Contact,
  Star, CalendarClock, Gift, Bell, SlidersHorizontal, Box, Layers,
  Truck, KeyRound, ClipboardCheck,
} from 'lucide-react';
import { useUser } from '../context/user-context';
import { useAppFlags } from '../hooks/use-app-flags';
import { useRegional } from '../context/regional-context';
import { TomSalonikaLockup, TomFooter } from './ui/brand-logo';

interface SidebarCoolProps {
  activeView: string;
  onViewChange: (view: string) => void;
  isOpen?: boolean;
  onClose?: () => void;
  badges?: Partial<Record<string, number>>;
  /** En desktop: si true, sidebar reducida a iconos (72px). Solo aplica >=lg. */
  collapsed?: boolean;
  /** Toggle del estado collapsed. Visible siempre que se proporcione. */
  onToggleCollapse?: () => void;
}

export function SidebarCool({
  activeView, onViewChange,
  isOpen = false, onClose,
  badges = {},
  collapsed = false,
  onToggleCollapse,
}: SidebarCoolProps) {
  const { canViewModule, user } = useUser();
  // Flags de módulos opcionales (Depuración → app_settings): Calificaciones y
  // Personal solo aparecen si el desarrollador los encendió.
  const flags = useAppFlags();
  const { t, operaColombia } = useRegional();

  // Etiquetas traducidas (oct-2026): la clave vive en i18n/diccionarios.
  const items: Array<{ id: string; icon: typeof Home; label: string }> = [
    { id: 'dashboard',      icon: Home,         label: t('nav.dashboard') },
    { id: 'kpis',           icon: Gauge,        label: t('nav.kpis') },
    { id: 'orders',         icon: ShoppingCart, label: t('nav.orders') },
    { id: 'reservations',   icon: Calendar,     label: t('nav.reservations') },
    { id: 'tables',         icon: LayoutGrid,   label: t('nav.tables') },
    { id: 'menu',           icon: BookOpen,     label: t('nav.menu') },
    // Combos: feature con flag (Features → Módulo Combos), oct-2026.
    ...(flags.combos ? [{ id: 'combos', icon: Layers, label: t('nav.combos') }] : []),
    { id: 'inventory',      icon: Package,      label: t('nav.inventory') },
    { id: 'purchases',      icon: TrendingDown, label: t('nav.purchases') },
    { id: 'activos',        icon: Box,          label: t('nav.activos') },
    // Aprobaciones (oct-2026): solicitudes del cajero → admin de sede.
    // El cajero no aprueba nada: para él es la lista de lo que pidió.
    { id: 'aprobaciones',   icon: ClipboardCheck, label: user?.role === 'cajera' ? t('nav.misSolicitudes') : t('nav.aprobaciones') },
    { id: 'contabilidad',   icon: Receipt,      label: t('nav.contabilidad') },
    { id: 'billing',        icon: FileText,     label: t('nav.billing') },
    { id: 'payments',       icon: Wallet,       label: t('nav.payments') },
    // Mensajeros Urbanos solo opera en Colombia: fuera de COP no aparece.
    ...(operaColombia ? [{ id: 'envios-mu', icon: Truck, label: t('nav.enviosMu') }] : []),
    { id: 'credenciales',   icon: KeyRound,     label: t('nav.credenciales') },
    { id: 'terceros',       icon: Contact,      label: t('nav.terceros') },
    { id: 'users',          icon: Users,        label: t('nav.users') },
    { id: 'reports',        icon: BarChart3,    label: t('nav.reports') },
    { id: 'params',         icon: Settings,     label: t('nav.params') },
    { id: 'legal-entities', icon: Building2,    label: t('nav.legalEntities') },
    // Módulos con flag — solo entran a la lista si están encendidos.
    ...(flags.cortesias ? [{ id: 'cortesias', icon: Gift,        label: t('nav.cortesias') }] : []),
    ...(flags.staff     ? [{ id: 'staff',   icon: CalendarClock, label: t('nav.staff') }] : []),
    ...(flags.ratings   ? [{ id: 'ratings', icon: Star,          label: t('nav.ratings') }] : []),
    // Notificaciones: superadmin + dev. Features: SOLO dev (canViewModule filtra).
    { id: 'notificaciones', icon: Bell,         label: t('nav.notificaciones') },
    { id: 'features',       icon: SlidersHorizontal, label: t('nav.features') },
    { id: 'debug',          icon: Bug,          label: t('nav.debug') },
    { id: 'technical-docs', icon: Code2,        label: t('nav.technicalDocs') },
  ];
  const visible = items.filter(it => canViewModule(it.id));

  const handleNav = (id: string) => {
    onViewChange(id);
    if (onClose) onClose();
  };

  return (
    <>
      {/* Backdrop mobile */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className={`fixed inset-0 bg-black/40 z-40 lg:hidden transition-opacity duration-200 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      />
      <aside
        className={`
          fixed lg:static top-0 left-0 z-50
          flex flex-col
          w-[min(280px,82vw)] ${collapsed ? 'lg:w-[72px]' : 'lg:w-[236px]'} flex-shrink-0
          h-full
          transform will-change-transform
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
        style={{
          // En mobile el aside está fuera del shell cool y necesita su propio
          // fondo azul (el body queda blanco). En desktop el padre ya es azul.
          background: 'var(--blue-grad)',
          // En collapsed reducimos el padding horizontal para que el icon
          // quede centrado en una columna de 72px.
          padding: collapsed ? '8px 12px 16px' : '8px 16px 16px',
          // Aside ya NO scrollea — sólo el bloque de items adentro lo hace.
          // Esto deja el botón Minimizar siempre visible al pie aunque la lista
          // tenga muchos items (pedido usuario jun-2026).
          overflow: 'hidden',
          transition: 'width 220ms cubic-bezier(.4,0,.2,1), transform 280ms cubic-bezier(.4,0,.2,1), padding 220ms',
        }}
      >
        {/* Header mobile — logo + close */}
        <div
          className="lg:hidden flex items-center"
          style={{
            gap: 12, padding: '8px 6px 12px',
            color: '#fff',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <TomSalonikaLockup size={19} />
            <div style={{
              marginTop: 3, fontSize: 11.5, fontWeight: 600,
              color: 'rgba(255,255,255,.72)', lineHeight: 1.25,
            }}>{t('app.subtitulo')}</div>
          </div>
          <button
            onClick={onClose}
            aria-label={t('sidebar.cerrarMenu')}
            className="press"
            style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'rgba(255,255,255,.16)',
              border: 0, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
          ><X className="w-4 h-4" /></button>
        </div>

        {/* El subtítulo "Sistema de gestión de restaurantes" ahora vive en el
            codo del topbar, justo debajo del logo (pedido del usuario). */}

        {/* Scroll de items — el botón Minimizar queda fuera (footer). */}
        <div
          className="flex flex-col"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            scrollbarWidth: 'thin' as const,
            // Hueco visual entre el último item y el footer.
            paddingBottom: 6,
          }}
        >
        {visible.map(item => {
          const Icon = item.icon;
          const isActive = activeView === item.id;
          const badge = badges[item.id];
          // En collapsed mostramos solo el icono centrado en un cuadrado 44x44.
          // El badge se convierte en un punto pequeño superpuesto (esquina sup
          // derecha) — el número exacto pasa al tooltip.
          if (collapsed) {
            return (
              <button
                key={item.id}
                onClick={() => handleNav(item.id)}
                title={typeof badge === 'number'
                  ? `${item.label} · ${badge > 9 ? '9+' : badge}`
                  : item.label}
                aria-label={item.label}
                className="press"
                style={{
                  width: 44, height: 44,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  alignSelf: 'center',
                  marginBottom: 4,
                  borderRadius: 13,
                  border: 0,
                  background: isActive ? '#fff' : 'transparent',
                  boxShadow: isActive ? '0 8px 18px rgba(8,46,92,.3)' : 'none',
                  color: isActive ? 'var(--blue-700)' : 'rgba(255,255,255,.84)',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background .12s, color .12s',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'rgba(255,255,255,.12)';
                    e.currentTarget.style.color = '#fff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'rgba(255,255,255,.84)';
                  }
                }}
              >
                <Icon className="w-[20px] h-[20px]" strokeWidth={2} />
                {typeof badge === 'number' && badge > 0 && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: 'absolute', top: 5, right: 5,
                      minWidth: 8, height: 8, borderRadius: 999,
                      background: 'var(--warn)',
                      border: '2px solid #1257A8',
                    }}
                  />
                )}
              </button>
            );
          }
          // Expanded — la pildora completa con label + número.
          return (
            <button
              key={item.id}
              onClick={() => handleNav(item.id)}
              className="press"
              style={{
                width: '100%',
                display: 'flex', alignItems: 'center', gap: 13,
                padding: '11px 15px',
                marginBottom: 2,
                borderRadius: 13,
                border: 0,
                background: isActive ? '#fff' : 'transparent',
                boxShadow: isActive ? '0 8px 18px rgba(8,46,92,.3)' : 'none',
                color: isActive ? 'var(--blue-700)' : 'rgba(255,255,255,.84)',
                fontFamily: 'inherit',
                fontSize: 14.5,
                fontWeight: isActive ? 700 : 600,
                cursor: 'pointer',
                textAlign: 'left',
                whiteSpace: 'nowrap',
                transition: 'background .12s, color .12s',
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = 'rgba(255,255,255,.12)';
                  e.currentTarget.style.color = '#fff';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'rgba(255,255,255,.84)';
                }
              }}
            >
              <Icon className="w-[20px] h-[20px] flex-shrink-0" strokeWidth={2} />
              <span style={{ flex: 1 }}>{item.label}</span>
              {typeof badge === 'number' && (
                <span
                  className="tabular-nums"
                  style={{
                    fontSize: 11, fontWeight: 800,
                    padding: '1px 7px',
                    borderRadius: 999,
                    background: isActive ? 'var(--blue-600)' : 'var(--warn)',
                    color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 18,
                  }}
                >{badge > 9 ? '9+' : badge}</span>
              )}
            </button>
          );
        })}

        </div>{/* fin scroll de items */}

        {/* Footer de marca — "funciona con TOM", enlaza a la web de la matriz. */}
        <TomFooter
          collapsed={collapsed}
          className="shrink-0"
          style={{
            marginTop: 10,
            padding: collapsed ? '8px 0 2px' : '10px 4px 2px',
            borderTop: '1px solid rgba(255,255,255,.12)',
          }}
        />

        {/* Toggle Colapsar — footer del aside, SIEMPRE visible (fuera del
            scroll). Sólo desktop. */}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="hidden lg:flex press"
            title={collapsed ? t('sidebar.expandirMenu') : t('sidebar.minimizarMenu')}
            aria-label={collapsed ? t('sidebar.expandirMenu') : t('sidebar.minimizarMenu')}
            style={{
              alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 10,
              marginTop: 8,
              width: collapsed ? 44 : '100%',
              height: collapsed ? 44 : 'auto',
              padding: collapsed ? 0 : '10px 14px',
              alignSelf: collapsed ? 'center' : 'stretch',
              borderRadius: 13,
              border: 0,
              background: 'rgba(255,255,255,.10)',
              color: '#fff',
              fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
              cursor: 'pointer',
              transition: 'background .12s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,.20)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,.10)'}
          >
            {collapsed
              ? <ChevronsRight className="w-[18px] h-[18px]" strokeWidth={2.2} />
              : <>
                  <ChevronsLeft className="w-[18px] h-[18px]" strokeWidth={2.2} />
                  <span>{t('sidebar.minimizar')}</span>
                </>
            }
          </button>
        )}
      </aside>
    </>
  );
}
